export type PricingPlan = {
  id: string
  name: string
  tagline: string
  monthlyPrice: number | null
  priceLabel?: string
  description: string
  features: string[]
  recommended?: boolean
}

/**
 * Live pricing from creditmanager.uk.com/pricing. Layout reads from this file
 * only, so prices can be changed here without touching any component.
 */
export const pricingPlans: PricingPlan[] = [
  {
    id: 'basic',
    name: 'BASIC',
    tagline: 'For small businesses invoicing at low volume',
    monthlyPrice: 45,
    description:
      'A great option if you raise a low number of invoices each month, are not struggling to get paid, but want to keep a close eye on the creditworthiness of your customers.',
    features: [
      'Up to 25 premium credit reports per year',
      'Unlimited credit summaries and alerts',
      'Automated chasing from a Satago email domain',
      'Selective invoice financing',
    ],
  },
  {
    id: 'advanced',
    name: 'ADVANCED',
    tagline: 'For businesses that need chasing done properly',
    monthlyPrice: 60,
    recommended: true,
    description:
      'Everything in Basic, with automated chasing sent from your own email domain so reminders carry your name and are far less likely to land in a junk folder.',
    features: [
      'Everything in BASIC',
      'Chasing sent from your own domain',
      'Up to 100 SMS reminders per month',
      'Up to 25 premium credit reports per year',
    ],
  },
  {
    id: 'premium',
    name: 'PREMIUM',
    tagline: 'For larger organisations at higher volume',
    monthlyPrice: 80,
    description:
      'Everything in Advanced, with the option to outsource chasing to us. Messages come from accounts@creditmanager.uk.com, which customers read as third-party escalation — and that usually means faster payment.',
    features: [
      'Everything in ADVANCED',
      'Chasing outsourced to Credit Manager',
      'Up to 100 premium credit reports per year',
      'Suited to high invoice volumes',
    ],
  },
  {
    id: 'caas',
    name: 'CREDIT CONTROL as a SERVICE',
    tagline: 'A bespoke service built around you',
    monthlyPrice: null,
    priceLabel: 'POA',
    description:
      'For customers looking for a bespoke Credit Control as a Service offering. Bookkeeping and Virtual Finance Director support can be provided alongside it.',
    features: [
      'Fully managed credit control',
      'Unlimited premium credit reports',
      'Bookkeeping available',
      'Virtual Finance Director available',
    ],
  },
]

export type DiscountCode = {
  code: string
  label: string
  planId: string
  percentOff: number
  note: string
}

export const discountCodes: DiscountCode[] = [
  {
    code: 'FORE',
    label: 'FORE Business',
    planId: 'advanced',
    percentOff: 33.3,
    note: 'Your FORE Business discount applies for the full 12-month contract period.',
  },
]

export type SelectedPlan = {
  planId: string
  name: string
  standardPrice: number | null
  price: number | null
  discountLabel?: string
  discountPercent?: number
}

export function findDiscount(code: string) {
  const normalised = code.trim().toUpperCase()
  if (!normalised) return undefined
  return discountCodes.find((entry) => entry.code.toUpperCase() === normalised)
}

export function discountedPrice(price: number, percentOff: number) {
  return Math.round(price * (1 - percentOff / 100) * 100) / 100
}

export function formatPrice(value: number | null) {
  if (value === null) return 'POA'
  return Number.isInteger(value)
    ? `£${value}`
    : `£${value.toFixed(2).replace(/\.00$/, '')}`
}

export function planSummaryLabel(plan: SelectedPlan) {
  return `${plan.name} · ${formatPrice(plan.price)} / month`
}

export function toSelectedPlan(
  plan: PricingPlan,
  discount?: DiscountCode,
): SelectedPlan {
  const applies =
    discount && discount.planId === plan.id && plan.monthlyPrice !== null
  return {
    planId: plan.id,
    name: plan.name,
    standardPrice: plan.monthlyPrice,
    price:
      applies && plan.monthlyPrice !== null
        ? discountedPrice(plan.monthlyPrice, discount.percentOff)
        : plan.monthlyPrice,
    discountLabel: applies ? discount.label : undefined,
    discountPercent: applies ? discount.percentOff : undefined,
  }
}
